import React, { useEffect, useState } from "react";
import { Copy, ExternalLink, Store } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Badge, Field, Panel, inputCls } from "../ui";

const PICKUP = [
  ["", "Not offered - listing only shows the part is in the shop"],
  ["buy", "Buy online, collect in store"],
  ["reserve", "Reserve online, pay in store"],
  ["not supported", "Explicitly no pickup"],
];

/** Free local listings: tells Google the surplus stock is physically in the Townsville shed. */
export default function LocalInventoryPanel() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    adminApi.get("/admin/shop/local-inventory").then((r) => setState(r.data)).catch(() => setState(null));

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put("/admin/shop/local-inventory", {
        store_code: state.store_code,
        pickup_method: state.pickup_method,
        pickup_sla: state.pickup_sla,
      });
      await load();
      toast.success("Store details saved", {
        description: "Add the local feed URL in Merchant Center to clear the warning.",
      });
    } catch (e) {
      toast.error("Could not save that");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  return (
    <Panel data-testid="local-inventory-panel">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider gold-text">
        <Store className="h-4 w-4" /> Free local listings
      </p>
      <p className="mt-2 text-sm leading-relaxed text-[color:var(--nq-stone)]">
        Merchant Center flags "missing local inventory data" because free local listings are
        switched on but Google has never been told what's on the shelf at 321 Ingham Rd. Put your
        store code in below, then add the local feed URL in Merchant Center under{" "}
        <em>Data sources &rarr; Add local inventory source</em>.
      </p>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Field
          label="Store code"
          hint="Merchant Center → Business info → Stores. Usually the code you gave your Business Profile listing."
        >
          <input
            value={state.store_code || ""}
            onChange={(e) => setState({ ...state, store_code: e.target.value })}
            placeholder="e.g. NQS-GARBUTT"
            className={inputCls}
            data-testid="local-store-code-input"
          />
        </Field>
        <Field label="Collection from the shop" hint="Leave as not offered unless you want online orders collected in store.">
          <select
            value={state.pickup_method || ""}
            onChange={(e) => setState({ ...state, pickup_method: e.target.value })}
            className={inputCls}
            data-testid="local-pickup-select"
          >
            {PICKUP.map(([v, label]) => (
              <option key={v} value={v} style={{ color: "#111" }}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {state.store_code ? (
          <Badge tone="green" data-testid="local-store-status">store code set</Badge>
        ) : (
          <Badge tone="red" data-testid="local-store-status">store code missing</Badge>
        )}
        <button onClick={save} disabled={busy} className="btn-gold px-4 py-2 text-xs disabled:opacity-50" data-testid="local-save-button">
          Save store details
        </button>
        <button
          onClick={() => {
            navigator.clipboard?.writeText(state.feed_url);
            toast.success("Local feed URL copied");
          }}
          className="btn-ghost inline-flex items-center gap-2 text-xs"
          data-testid="copy-local-feed-url"
        >
          <Copy className="h-3.5 w-3.5" /> Copy local feed URL
        </button>
        <a href={state.feed_url} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 text-xs">
          <ExternalLink className="h-3.5 w-3.5" /> View local feed
        </a>
      </div>
    </Panel>
  );
}
